import { IssueSeverity, IssueStatusType } from '@kcftech/prometheus-client';
import {
    DashboardTimeRangePreset,
    DashboardTimeRangePresets,
    presetToDateRange,
} from 'pages/Dashboard/DeskActivityOverviewDashboard/components/TimeRangeFilterButton';
import {
    createHashStore,
    HashStoreConfig,
    serializers,
} from 'pages/Dashboard/utils/createHashStore';

export type DashboardFiltersState = {
    startDateTime: Date;
    endDateTime: Date | undefined;
    locationIds: string[];
    severities: IssueSeverity[];
    statuses: IssueStatusType[];
    faultTypeIds: string[];
    timeRangePreset: DashboardTimeRangePreset;
};

export type DashboardFiltersActions = {
    setStartDateTime: (date: Date) => void;
    setEndDateTime: (date: Date | undefined) => void;
    setLocationIds: (ids: string[]) => void;
    setSeverities: (severities: IssueSeverity[]) => void;
    setStatuses: (statuses: IssueStatusType[]) => void;
    setFaultTypeIds: (ids: string[]) => void;
    setTimeRangePreset: (preset: DashboardTimeRangePreset | undefined) => void;
    resetFilters: () => void;
};

export type DashboardFiltersStore = DashboardFiltersState & DashboardFiltersActions;

const getDefaultState = (): DashboardFiltersState => ({
    startDateTime: (() => {
        const { startDateTime } = presetToDateRange('last-year');
        return startDateTime;
    })(),
    endDateTime: undefined,
    locationIds: [],
    severities: [],
    statuses: [],
    faultTypeIds: [],
    timeRangePreset: 'last-year',
});

const hashConfig: HashStoreConfig<DashboardFiltersState> = {
    startDateTime: {
        paramName: 'startDate',
        ...serializers.date,
    },
    endDateTime: {
        paramName: 'endDate',
        ...serializers.optionalDate,
    },
    locationIds: {
        paramName: 'locations',
        ...serializers.stringArray,
    },
    severities: {
        paramName: 'severities',
        ...serializers.enumArray<IssueSeverity>(Object.values(IssueSeverity)),
    },
    statuses: {
        paramName: 'statuses',
        ...serializers.enumArray<IssueStatusType>(Object.values(IssueStatusType)),
    },
    faultTypeIds: {
        paramName: 'faultTypes',
        ...serializers.stringArray,
    },
    timeRangePreset: {
        paramName: 'timeRange',
        ...serializers.enum<DashboardTimeRangePreset>(DashboardTimeRangePresets),
    },
};

export const useDashboardFiltersStore = createHashStore<
    DashboardFiltersState,
    DashboardFiltersActions
>(
    getDefaultState,
    hashConfig,
    (set, _get) => ({
        // Initialize from hash or defaults (handled by createHashStore)
        ...getDefaultState(),

        // Actions
        setStartDateTime: (date) => set({ startDateTime: date }),
        setEndDateTime: (date) => set({ endDateTime: date }),
        setLocationIds: (ids) => set({ locationIds: ids }),
        setSeverities: (severities) => set({ severities }),
        setStatuses: (statuses) => set({ statuses }),
        setFaultTypeIds: (ids) => set({ faultTypeIds: ids }),
        setTimeRangePreset: (preset) => set({ timeRangePreset: preset }),
        resetFilters: () => set(getDefaultState()),
    }),
    'dashboard-filters',
);
